import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { area, curveLinear, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { ChartHScroll, formatCurrency, formatK, MIN_PX_PER_CALENDAR_YEAR, thinTicks, useChartPlotWidth } from './d3';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';
import {
  INTEREST_COLOR,
  PRINCIPAL_COLOR,
  type CompoundYearRow,
} from '../../utils/investmentCompounding';

type PrincipalInterestChartProps = {
  rows: CompoundYearRow[];
  height?: number;
  title?: string;
};

const MARGIN = { top: 18, right: 14, bottom: 28, left: 44 };
/** Min hit-target width so year dots are easy to click. */
const HIT_MIN = 20;

type StackPoint = {
  year: number;
  principal: number;
  interest: number;
  total: number;
  index: number;
};

export function PrincipalInterestChart({
  rows,
  height = 220,
  title,
}: PrincipalInterestChartProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const points: StackPoint[] = useMemo(
    () =>
      rows.map((r, index) => ({
        year: r.year,
        principal: r.principalInvested,
        interest: r.cumulativeGrowth,
        total: r.endBalance,
        index,
      })),
    [rows],
  );

  const minYear = points[0]?.year ?? 0;
  const maxYear = points[points.length - 1]?.year ?? 0;
  const maxY = Math.max(...points.map((p) => p.total), 1);
  const { plotWidth: width, scrolls, onLayout } = useChartPlotWidth({
    unitCount: Math.max(maxYear - minYear, 1),
    minPxPerUnit: MIN_PX_PER_CALENDAR_YEAR,
    leftMargin: MARGIN.left,
    rightMargin: MARGIN.right,
  });

  const safeIndex =
    selectedIndex != null && selectedIndex >= 0 && selectedIndex < points.length
      ? selectedIndex
      : Math.max(points.length - 1, 0);
  const active = points[safeIndex];

  const model = useMemo(() => {
    if (points.length === 0 || width <= 0) {
      return {
        principalD: '',
        interestD: '',
        totalD: '',
        gridLines: [] as { y: number }[],
        yTicks: [] as { value: number; y: number }[],
        xTicks: [] as { year: number; x: number }[],
        dots: [] as { index: number; year: number; x: number; yTotal: number; yPrincipal: number }[],
      };
    }

    const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 1);
    const plotHeight = Math.max(height - MARGIN.top - MARGIN.bottom, 1);
    const yMax = maxY * 1.02;

    const xScale = scaleLinear()
      .domain([minYear, maxYear])
      .range([MARGIN.left, MARGIN.left + plotWidth]);

    const yScale = scaleLinear()
      .domain([0, yMax])
      .range([MARGIN.top + plotHeight, MARGIN.top]);

    const principalArea = area<StackPoint>()
      .x((d) => xScale(d.year))
      .y0(yScale(0))
      .y1((d) => yScale(d.principal))
      .curve(curveLinear);

    const interestArea = area<StackPoint>()
      .x((d) => xScale(d.year))
      .y0((d) => yScale(d.principal))
      .y1((d) => yScale(d.total))
      .curve(curveLinear);

    const totalLine = line<StackPoint>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.total))
      .curve(curveLinear);

    const dots = points.map((p) => ({
      index: p.index,
      year: p.year,
      x: xScale(p.year),
      yTotal: yScale(p.total),
      yPrincipal: yScale(p.principal),
    }));

    const yTickValues = yScale.ticks(4);
    const xTickYears = thinTicks(
      points.map((p) => p.year),
      plotWidth,
    );

    return {
      principalD: principalArea(points) ?? '',
      interestD: interestArea(points) ?? '',
      totalD: totalLine(points) ?? '',
      gridLines: yTickValues.map((tick) => ({ y: yScale(tick) })),
      yTicks: yTickValues.map((value) => ({ value, y: yScale(value) })),
      xTicks: xTickYears.map((year) => ({ year, x: xScale(year) })),
      dots,
    };
  }, [width, height, points, minYear, maxYear, maxY]);

  if (points.length === 0 || !active) {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyText}>No projection data yet</Text>
      </View>
    );
  }

  const hitWidth =
    model.dots.length > 1
      ? Math.max(HIT_MIN, model.dots[1].x - model.dots[0].x)
      : HIT_MIN * 1.5;

  return (
    <View style={styles.wrap}>
      {title ? <Text style={styles.title}>{title}</Text> : null}
      <Text style={styles.hint}>
        Blue area = principal (money you invest). Green area = interest earned on top. Click a year
        on the chart for details.
      </Text>

      <ChartHScroll
        height={height}
        plotWidth={width}
        scrolls={scrolls}
        onLayout={onLayout}
        style={styles.chartArea}
        axisWidth={MARGIN.left}
        axis={
          <>
            <Text style={styles.yCaption}>$</Text>
            {model.yTicks.map((tick) => (
              <Text
                key={`y-${tick.value}`}
                style={[styles.yTick, { top: tick.y - 7 }]}
              >
                {formatK(tick.value)}
              </Text>
            ))}
          </>
        }
      >
        {width > 0 && (
          <>
            <SvgChartCanvas
              width={width}
              height={height}
              style={{ pointerEvents: 'none' }}
            >
              {model.gridLines.map((g, i) => (
                <line
                  key={`grid-${i}`}
                  x1={MARGIN.left}
                  x2={width - MARGIN.right}
                  y1={g.y}
                  y2={g.y}
                  stroke={colors.border}
                  strokeWidth={1}
                  opacity={0.4}
                />
              ))}
              <line
                x1={MARGIN.left}
                y1={MARGIN.top}
                x2={MARGIN.left}
                y2={height - MARGIN.bottom}
                stroke={colors.textMuted}
                strokeWidth={1}
              />
              <line
                x1={MARGIN.left}
                y1={height - MARGIN.bottom}
                x2={width - MARGIN.right}
                y2={height - MARGIN.bottom}
                stroke={colors.textMuted}
                strokeWidth={1}
              />
              {model.yTicks.map((tick) => (
                <line
                  key={`yt-${tick.value}`}
                  x1={MARGIN.left - 4}
                  y1={tick.y}
                  x2={MARGIN.left}
                  y2={tick.y}
                  stroke={colors.textMuted}
                  strokeWidth={1}
                />
              ))}
              {model.xTicks.map((tick) => (
                <line
                  key={`xt-${tick.year}`}
                  x1={tick.x}
                  y1={height - MARGIN.bottom}
                  x2={tick.x}
                  y2={height - MARGIN.bottom + 4}
                  stroke={colors.textMuted}
                  strokeWidth={1}
                />
              ))}
              <path d={model.principalD} fill={PRINCIPAL_COLOR} opacity={0.4} />
              <path d={model.interestD} fill={INTEREST_COLOR} opacity={0.45} />
              <path d={model.totalD} fill="none" stroke={colors.text} strokeWidth={2} />
              {model.dots.map((d) => {
                const selected = active.index === d.index;
                return (
                  <g key={`dot-${d.index}`}>
                    <circle
                      cx={d.x}
                      cy={d.yPrincipal}
                      r={selected ? 4 : 2.5}
                      fill={PRINCIPAL_COLOR}
                    />
                    <circle
                      cx={d.x}
                      cy={d.yTotal}
                      r={selected ? 5 : 3}
                      fill={colors.text}
                    />
                  </g>
                );
              })}
              {model.xTicks.map((tick) => (
                <text
                  key={`xl-${tick.year}`}
                  x={tick.x}
                  y={height - 8}
                  textAnchor="middle"
                  fill={colors.text}
                  fontSize={10}
                  fontWeight={600}
                  fontFamily="IBM Plex Sans, sans-serif"
                >
                  {tick.year}
                </text>
              ))}
            </SvgChartCanvas>
            {/* Vertical hit strips — locationX is unreliable on RN web. */}
            {model.dots.map((d) => (
              <Pressable
                key={`hit-${d.index}`}
                accessibilityRole="button"
                accessibilityLabel={`Select year ${d.year}`}
                onPress={() => setSelectedIndex(d.index)}
                style={[
                  styles.hit,
                  {
                    left: d.x - hitWidth / 2,
                    width: hitWidth,
                    height: height - MARGIN.bottom,
                  },
                ]}
              />
            ))}
          </>
        )}
      </ChartHScroll>
      <Text style={styles.axisTitle}>Year</Text>

      <View style={styles.tooltip}>
        <Text style={styles.tooltipYear}>{active.year}</Text>
        <View style={styles.tooltipRow}>
          <Text style={[styles.tooltipLabel, { color: PRINCIPAL_COLOR }]}>Principal</Text>
          <Text style={styles.tooltipValue}>{formatCurrency(active.principal)}</Text>
        </View>
        <View style={styles.tooltipRow}>
          <Text style={[styles.tooltipLabel, { color: INTEREST_COLOR }]}>Interest</Text>
          <Text style={styles.tooltipValue}>{formatCurrency(active.interest)}</Text>
        </View>
        <View style={styles.tooltipRow}>
          <Text style={styles.tooltipLabel}>Total</Text>
          <Text style={[styles.tooltipValue, styles.tooltipTotal]}>
            {formatCurrency(active.total)}
          </Text>
        </View>
        <Text style={styles.tooltipCheck}>
          Check: principal + interest = {formatCurrency(active.principal + active.interest)}
        </Text>
      </View>

      <View style={styles.yearChips}>
        {points
          .filter(
            (p) =>
              p.index === 0 ||
              p.index === points.length - 1 ||
              p.index % 2 === 0 ||
              p.index === active.index,
          )
          .map((p) => (
            <Pressable
              key={p.year}
              onPress={() => setSelectedIndex(p.index)}
              style={[styles.yearChip, active.index === p.index && styles.yearChipActive]}
            >
              <Text
                style={[
                  styles.yearChipText,
                  active.index === p.index && styles.yearChipTextActive,
                ]}
              >
                {p.year}
              </Text>
            </Pressable>
          ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  title: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  chartArea: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  hit: {
    position: 'absolute',
    top: 0,
    zIndex: 2,
    cursor: 'pointer' as const,
  },
  yCaption: {
    position: 'absolute',
    top: 2,
    left: 4,
    width: MARGIN.left - 8,
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'right',
  },
  yTick: {
    position: 'absolute',
    left: 4,
    width: MARGIN.left - 8,
    ...typography.caption,
    color: colors.text,
    fontSize: 10,
    fontVariant: ['tabular-nums'],
    textAlign: 'right',
  },
  axisTitle: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  tooltip: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    padding: spacing.md,
    gap: 6,
  },
  tooltipYear: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
    marginBottom: 2,
  },
  tooltipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  tooltipLabel: {
    ...typography.caption,
    color: colors.textMuted,
  },
  tooltipValue: {
    ...typography.caption,
    color: colors.text,
    fontVariant: ['tabular-nums'],
    fontWeight: '600',
  },
  tooltipTotal: {
    color: colors.accent,
  },
  tooltipCheck: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 4,
    fontSize: 11,
  },
  yearChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  yearChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    cursor: 'pointer' as const,
  },
  yearChipActive: {
    borderColor: colors.accent,
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  yearChipText: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  yearChipTextActive: {
    color: colors.accent,
    fontWeight: '600',
  },
  empty: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptyText: {
    ...typography.body,
    color: colors.textMuted,
  },
});
